import React from 'react';
import { TrendingDown, Award } from 'lucide-react';
import { SIX_PILLARS } from '../data/pillars';

const SIZE = 320;
const CENTER = SIZE / 2;
const RADIUS = 115;
const RINGS = [0.25, 0.5, 0.75, 1];

const pointFor = (index, total, ratio) => {
  const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
  return [CENTER + Math.cos(angle) * RADIUS * ratio, CENTER + Math.sin(angle) * RADIUS * ratio];
};

const polygon = (ratios) =>
  ratios.map((r, i) => pointFor(i, ratios.length, r).join(',')).join(' ');

/** Six-pillar radar. `pillars` = [{ pillar_number, name, score }] */
export const PillarRadarChart = ({ pillars, weakest, hint, overallScore, peer }) => {
  const data = (pillars?.length ? pillars : SIX_PILLARS.map((p) => ({
    pillar_number: p.id,
    name: p.name,
    score: null,
  })));

  const ratios = data.map((p) => Math.max(0.04, Math.min(1, (p.score ?? 0) / 100)));
  const hasScores = data.some((p) => p.score !== null && p.score !== undefined);
  const peerAvailable = !!peer?.available;
  const peerRatios = peerAvailable
    ? data.map((p) => {
        const value = peer.pillar_averages?.[p.pillar_number] ?? peer.pillar_averages?.[String(p.pillar_number)];
        return Math.max(0.04, Math.min(1, (value ?? 0) / 100));
      })
    : null;

  return (
    <div className="flex flex-col lg:flex-row items-center gap-8" data-testid="pillar-radar-chart">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-[300px] h-[300px] shrink-0">
        {RINGS.map((r) => (
          <polygon
            key={r}
            points={polygon(data.map(() => r))}
            fill="none"
            stroke="#E2E8F0"
            strokeWidth="1"
          />
        ))}
        {data.map((p, i) => {
          const [x, y] = pointFor(i, data.length, 1);
          return <line key={p.pillar_number} x1={CENTER} y1={CENTER} x2={x} y2={y} stroke="#E2E8F0" strokeWidth="1" />;
        })}

        {hasScores && (
          <polygon
            points={polygon(ratios)}
            fill="rgba(79,70,229,0.22)"
            stroke="#4F46E5"
            strokeWidth="2.5"
            style={{ transition: 'all 600ms ease' }}
          />
        )}

        {peerRatios && (
          <polygon
            points={polygon(peerRatios)}
            fill="none"
            stroke="#F59E0B"
            strokeWidth="2"
            strokeDasharray="6 4"
          />
        )}

        {data.map((p, i) => {
          const [x, y] = pointFor(i, data.length, ratios[i]);
          const isWeakest = weakest && weakest.pillar_number === p.pillar_number;
          return (
            <circle
              key={`pt-${p.pillar_number}`}
              cx={x}
              cy={y}
              r={isWeakest ? 7 : 4.5}
              fill={isWeakest ? '#DC2626' : '#4F46E5'}
              stroke="#ffffff"
              strokeWidth="2"
            />
          );
        })}

        {data.map((p, i) => {
          const [x, y] = pointFor(i, data.length, 1.3);
          return (
            <text
              key={`lbl-${p.pillar_number}`}
              x={x}
              y={y}
              textAnchor={x > CENTER + 6 ? 'start' : x < CENTER - 6 ? 'end' : 'middle'}
              dominantBaseline="middle"
              fontSize="9.5"
              fontWeight="700"
              fill="#475569"
            >
              {`P${p.pillar_number}`}
            </text>
          );
        })}
      </svg>

      <div className="flex-1 w-full space-y-2">
        {overallScore != null && (
          <div className="flex items-center gap-2 mb-3">
            <Award className="h-5 w-5 text-indigo-600" />
            <span className="text-base font-bold text-gray-900" data-testid="radar-overall-score">
              Overall CIS {overallScore}
            </span>
            {peerAvailable && (
              <span className="text-sm text-amber-700 font-semibold" data-testid="radar-peer-overall">
                · peers {peer.overall_average}
              </span>
            )}
          </div>
        )}

        {peerAvailable ? (
          <div className="flex items-center gap-4 mb-3 text-xs font-semibold text-gray-600" data-testid="radar-peer-legend">
            <span className="flex items-center gap-2">
              <span className="inline-block w-5 h-[3px] bg-indigo-600 rounded"></span> You
            </span>
            <span className="flex items-center gap-2">
              <span className="inline-block w-5 border-t-2 border-dashed border-amber-500"></span>
              {peer.basis === 'size' ? `Certified peers · ${peer.organization_size}` : 'All certified peers'} ({peer.peer_count})
            </span>
          </div>
        ) : (
          peer && (
            <p className="text-xs text-gray-500 mb-3" data-testid="radar-peer-unavailable">
              Not enough certified peers yet for a comparison.
              {peer.detail ? ` ${peer.detail}` : ''}
            </p>
          )
        )}
        {data.map((p) => {
          const isWeakest = weakest && weakest.pillar_number === p.pillar_number;
          return (
            <div
              key={`row-${p.pillar_number}`}
              className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 transition-colors ${
                isWeakest ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
              }`}
              data-testid={`radar-pillar-row-${p.pillar_number}`}
            >
              <span className="text-sm font-medium text-gray-800">
                <span className="text-gray-400 mr-2 font-bold">P{p.pillar_number}</span>
                {p.name}
              </span>
              <span className="flex items-center gap-3">
                <span className={`text-sm font-bold ${isWeakest ? 'text-red-600' : 'text-indigo-600'}`}>
                  {p.score == null ? '—' : p.score}
                </span>
                {peerAvailable && (
                  <span className="text-xs font-semibold text-amber-700 w-10 text-right">
                    {peer.pillar_averages?.[p.pillar_number] ?? peer.pillar_averages?.[String(p.pillar_number)] ?? '—'}
                  </span>
                )}
              </span>
            </div>
          );
        })}

        {hint && (
          <div className="mt-4 flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid="radar-improvement-hint">
            <TrendingDown className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900 leading-relaxed">{hint}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PillarRadarChart;
